/**
 * Image Upload with Drag & Drop and Cropping Component
 * Provides drag/drop upload, image cropping, and preview functionality
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  PhotoIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  ScissorsIcon
} from '@heroicons/react/24/outline';

const ImageUploadCrop = ({
  onImageSelect,
  initialImage = null,
  aspectRatio = null, // e.g. 16/9 for widescreen, 1 for square
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  cropEnabled = true,
  className = ""
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(initialImage);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const imgRef = useRef();
  const inputRef = useRef();
  const canvasRef = useRef();

  // Update imageUrl when initialImage prop changes
  useEffect(() => {
    setImageUrl(initialImage);
  }, [initialImage]);

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  // Handle drop event
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validate file
  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      setError(`File type not supported. Please use: ${allowedTypes.join(', ')}`);
      return false;
    }

    if (file.size > maxFileSize) {
      setError(`File size too large. Maximum size: ${Math.round(maxFileSize / 1024 / 1024)}MB`);
      return false;
    }

    setError('');
    return true;
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    console.log('📁 [ImageUploadCrop] handleFileSelect called with file:', {
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type
    });

    if (!validateFile(file)) return;

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      console.log('📖 [ImageUploadCrop] File read complete, calling processImage');
      setImageUrl(dataUrl);

      // Process image directly without auto-opening crop modal
      processImage(dataUrl, file);

      // Prepare default crop settings for when crop button is clicked
      if (cropEnabled) {
        if (aspectRatio) {
          const defaultCrop = {
            unit: '%',
            width: 90,
            height: 90 / aspectRatio * 100, // Convert to percentage
            x: 5,
            y: 5
          };
          setCrop(defaultCrop);
          setCompletedCrop(defaultCrop);
        } else {
          // Default crop without aspect ratio
          const defaultCrop = {
            unit: '%',
            width: 90,
            height: 90,
            x: 5,
            y: 5
          };
          setCrop(defaultCrop);
          setCompletedCrop(defaultCrop);
        }
      }
    };
    reader.readAsDataURL(file);
  };

  // Process and finalize image
  const processImage = async (imageDataUrl, file) => {
    console.log('🎯 [ImageUploadCrop] processImage called with:', {
      hasImageDataUrl: !!imageDataUrl,
      hasFile: !!file,
      fileName: file?.name,
      fileSize: file?.size,
      hasOnImageSelect: typeof onImageSelect === 'function'
    });

    setUploading(true);
    try {
      // Update the preview with the cropped/processed image
      setImageUrl(imageDataUrl);

      console.log('🚀 [ImageUploadCrop] Calling onImageSelect callback...');
      // In a real implementation, you'd upload to your server/cloud storage
      // For now, we'll just pass the data URL
      await onImageSelect({
        file: file,
        dataUrl: imageDataUrl,
        name: file.name,
        size: file.size,
        type: file.type
      });
      console.log('✅ [ImageUploadCrop] onImageSelect callback completed');

      // Only close crop modal if it's currently open (from manual crop operation)
      // Don't close if we're just processing the initial upload
      if (showCropModal) {
        setShowCropModal(false);
      }
    } catch (err) {
      console.error('Error in processImage:', err);
      setError('Failed to process image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Get cropped image canvas
  const getCroppedImg = useCallback(() => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    let crop = completedCrop;

    // Convert percentage-based crop to pixel-based
    if (crop.unit === '%') {
      crop = {
        ...crop,
        x: (crop.x / 100) * image.width,
        y: (crop.y / 100) * image.height,
        width: (crop.width / 100) * image.width,
        height: (crop.height / 100) * image.height,
        unit: 'px'
      };
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;

    // Set canvas dimensions
    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    // Draw the cropped image
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return canvas;
  }, [completedCrop]);

  // Handle crop confirmation
  const handleCropConfirm = async () => {
    // Store the file reference to prevent it from being lost
    const fileToProcess = selectedFile;

    // If no crop is made, use the current crop or fall back to full image
    const cropToUse = completedCrop || crop;

    if (!cropToUse && fileToProcess) {
      // No crop area defined, use original image
      await processImage(imageUrl, fileToProcess);
      return;
    }

    const canvas = getCroppedImg();

    if (canvas) {
      if (!fileToProcess) {
        setError('File reference lost. Please try uploading again.');
        return;
      }

      canvas.toBlob(
        async (blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onload = async (e) => {
              try {
                await processImage(e.target.result, new File([blob], fileToProcess.name, {
                  type: fileToProcess.type
                }));
              } catch (error) {
                console.error('Error processing cropped image:', error);
                setError('Failed to process cropped image. Please try again.');
              }
            };
            reader.readAsDataURL(blob);
          } else {
            setError('Failed to process cropped image. Please try again.');
          }
        },
        fileToProcess.type || 'image/jpeg',
        0.9
      );
    } else {
      setError('Unable to process crop. Please try again.');
    }
  };

  // Handle input file change
  const handleInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Remove image
  const handleRemove = () => {
    setImageUrl(null);
    setSelectedFile(null);
    setShowCropModal(false);
    setError('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onImageSelect(null);
  };

  // Reset crop
  const resetCrop = () => {
    if (aspectRatio) {
      const defaultCrop = {
        unit: '%',
        width: 90,
        height: 90 / aspectRatio * 100,
        x: 5,
        y: 5
      };
      setCrop(defaultCrop);
      setCompletedCrop(defaultCrop);
    } else {
      const defaultCrop = {
        unit: '%',
        width: 90,
        height: 90,
        x: 5,
        y: 5
      };
      setCrop(defaultCrop);
      setCompletedCrop(defaultCrop);
    }
  };

  return (
    <div className={className}>
      {/* Main Upload Area */}
      {!imageUrl ? (
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : error
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <PhotoIcon className={`mx-auto h-12 w-12 ${error ? 'text-red-400' : 'text-gray-400'}`} />

          <div className="mt-4">
            <label className="relative cursor-pointer">
              <span className={`font-medium ${error ? 'text-red-600' : 'text-blue-600 hover:text-blue-500'}`}>
                Click to upload
              </span>
              <input
                ref={inputRef}
                type="file"
                accept={allowedTypes.join(',')}
                onChange={handleInputChange}
                className="sr-only"
              />
            </label>
            <p className="text-gray-500"> or drag and drop</p>
          </div>

          <p className="text-xs text-gray-500 mt-2">
            {allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')} up to {Math.round(maxFileSize / 1024 / 1024)}MB
          </p>

          {error && (
            <p className="text-sm text-red-600 mt-2">{error}</p>
          )}

          {dragActive && (
            <div className="absolute inset-0 bg-blue-100 bg-opacity-50 flex items-center justify-center rounded-lg">
              <p className="text-blue-600 font-medium">Drop your image here</p>
            </div>
          )}
        </div>
      ) : (
        /* Image Preview */
        <div className="relative">
          <img
            src={imageUrl}
            alt="Upload preview"
            className="w-full h-64 object-cover rounded-lg border border-gray-300"
          />
          <div className="absolute top-2 right-2 flex space-x-2">
            {cropEnabled && (
              <button
                type="button"
                onClick={() => setShowCropModal(true)}
                className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors shadow-lg"
                title="Crop Image"
              >
                <ScissorsIcon className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={handleRemove}
              className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
              title="Remove Image"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {showCropModal && imageUrl && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Crop Image
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={resetCrop}
                      className="p-2 text-gray-400 hover:text-gray-600"
                      title="Reset Crop"
                    >
                      <ArrowPathIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCropModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="max-h-96 overflow-auto">
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={aspectRatio}
                    className="max-w-full"
                  >
                    <img
                      ref={imgRef}
                      src={imageUrl}
                      alt="Crop preview"
                      className="max-w-full h-auto"
                    />
                  </ReactCrop>
                </div>

                {/* Hidden canvas for crop processing */}
                <canvas
                  ref={canvasRef}
                  className="hidden"
                />

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCropModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCropConfirm}
                    disabled={uploading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {uploading ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="h-4 w-4 mr-2" />
                        Apply Crop
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadCrop;